import { createMongoAbility, type RawRule } from '@casl/ability';
import type { Ability } from 'generated/gql/account-manager/graphql';
import { useTeamMemberAbilities } from 'hooks/useTeamMemberAbilities.am';
import { AbilityContext } from './AbilityContext';

// Populate the ability context with the current user's abilities
function AbilityProvider({ children }: { children: React.ReactNode }) {
  const abilitiesQuery = useTeamMemberAbilities();
  const abilities = abilitiesQuery.data?.teamMemberAbility ?? [];

  return (
    <AbilityContext.Provider
      value={createMongoAbility(abilityIdsToConditions(abilities))}
    >
      {children}
    </AbilityContext.Provider>
  );
}

/**
 * Convert the authorized instance IDs from AM into a `conditions` format accepted by CASL.
 *
 * ```
 * // This:
 * { action: ['destroy'], subject: ['SeoKeyword'], limitingIds: [1, 2, 3] },
 * { action: ['read'], subject: ['Site'], limitingIds: [1, 2, 3] },
 * // Becomes:
 * { action: ['destroy'], subject: ['SeoKeyword'], conditions: { id: { $in: [1, 2, 3] } } },
 * { action: ['read'], subject: ['Site'], conditions: { platform_site_id: { $in: [1, 2, 3] } } }
 * ```
 */
function abilityIdsToConditions(abilities: Ability[]): RawRule[] {
  return abilities.map(({ limitingIds, ...ability }) => {
    if (!limitingIds) return ability;

    const idField = ability.subject.includes('Site')
      ? 'platform_site_id'
      : 'id';

    return {
      ...ability,
      conditions: { [idField]: { $in: limitingIds } },
    };
  });
}

export { AbilityProvider };
